import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import { Trash04, Edit02 } from '@untitledui-pro/icons/line';

import { deleteCamera, listCameras, updateCamera, CameraDto, CameraId } from '@api/camera';
import useNotificationStore from '../../../store/notification';
import { NotificationType } from '@visualizer/types';

type EditingState = {
    id: string;
    name: string;
    originalName: string;
};

type CameraDialogProps = {
    open: boolean;
    onClose: () => void;
    onSelectCamera: (cameraId: CameraId) => Promise<void> | void;
};

const CameraDialog: React.FC<CameraDialogProps> = ({ open, onClose, onSelectCamera }) => {
    const [cameras, setCameras] = useState<CameraDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<EditingState | null>(null);
    const [renameInFlight, setRenameInFlight] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const showNotification = useNotificationStore((state) => state.showNotification);

    const resetState = useCallback(() => {
        setError(null);
        setEditing(null);
        setRenameInFlight(false);
        setDeletingId(null);
    }, []);

    const fetchCameras = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await listCameras();
            setCameras(response);
        } catch (apiError) {
            console.error('Failed to fetch cameras', apiError);
            setError('Unable to load cameras');
            setCameras([]);
            showNotification('Unable to load cameras', NotificationType.Error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }

        resetState();
        fetchCameras();
    }, [fetchCameras, open, resetState]);

    const commitRename = useCallback(async () => {
        if (!editing) {
            return;
        }

        const trimmedName = editing.name.trim();
        if (!trimmedName) {
            setEditing(null);
            return;
        }

        if (trimmedName === editing.originalName) {
            setEditing(null);
            return;
        }

        try {
            setRenameInFlight(true);
            const updatedCamera = await updateCamera(editing.id, { name: trimmedName });
            setCameras((existing) =>
                existing.map((camera) =>
                    String(camera.id) === String(updatedCamera.id) ? { ...camera, name: updatedCamera.name } : camera
                )
            );
            setEditing(null);
            showNotification('Camera renamed', NotificationType.Success);
        } catch (apiError) {
            console.error('Failed to rename camera', apiError);
            setError('Unable to rename camera');
            showNotification('Unable to rename camera', NotificationType.Error);
        } finally {
            setRenameInFlight(false);
        }
    }, [editing]);

    const handleRenameKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            void commitRename();
        } else if (event.key === 'Escape') {
            setEditing(null);
        }
    };

    const handleStartRename = (camera: CameraDto) => {
        setEditing({
            id: String(camera.id),
            name: camera.name,
            originalName: camera.name,
        });
    };

    const handleDelete = async (camera: CameraDto, mouseEvent: React.MouseEvent) => {
        mouseEvent.stopPropagation();
        const id = String(camera.id);
        setDeletingId(id);
        setError(null);
        try {
            await deleteCamera(id);
            setCameras((existing) => existing.filter((item) => String(item.id) !== id));
            showNotification('Camera deleted', NotificationType.Success);
        } catch (apiError) {
            console.error('Failed to delete camera', apiError);
            setError('Unable to delete camera');
            showNotification('Unable to delete camera', NotificationType.Error);
        } finally {
            setDeletingId(null);
        }
    };

    const handleSelectCamera = async (camera: CameraDto) => {
        try {
            await onSelectCamera(String(camera.id));
            onClose();
        } catch (apiError) {
            console.error('Failed to select camera', apiError);
            setError('Unable to load camera');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Load camera</DialogTitle>
            <DialogContent>
                {error && (
                    <Box px={2} py={1}>
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    </Box>
                )}
                {loading ? (
                    <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                    </Box>
                ) : cameras.length === 0 ? (
                    <Box px={2} py={3}>
                        <Typography variant="body2" color="text.secondary">
                            No saved cameras yet.
                        </Typography>
                    </Box>
                ) : (
                    <List>
                        {cameras.map((camera) => {
                            const id = String(camera.id);
                            const isEditing = editing?.id === id;
                            const isDeleting = deletingId === id;

                            return (
                                <ListItem
                                    key={id}
                                    disablePadding
                                    secondaryAction={
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <IconButton
                                                edge="end"
                                                aria-label="rename"
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    handleStartRename(camera);
                                                }}
                                                disabled={isEditing || renameInFlight || isDeleting}
                                            >
                                                <Edit02 className="icon-size-medium" />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                size="small"
                                                onClick={(event) => handleDelete(camera, event)}
                                                disabled={isDeleting || renameInFlight}
                                            >
                                                {isDeleting ? <CircularProgress size={16} /> : <Trash04 className="icon-size-medium" />}
                                            </IconButton>
                                        </Box>
                                    }
                                >
                                    {isEditing ? (
                                        <Box flex={1} pr={1}>
                                            <TextField
                                                autoFocus
                                                fullWidth
                                                size="small"
                                                value={editing.name}
                                                onChange={(event) =>
                                                    setEditing((state) => (state ? { ...state, name: event.target.value } : state))
                                                }
                                                onBlur={() => void commitRename()}
                                                onKeyDown={handleRenameKeyDown}
                                                disabled={renameInFlight}
                                            />
                                        </Box>
                                    ) : (
                                        <ListItemButton
                                            onClick={(event) => {
                                                if (event.detail > 1 || renameInFlight) {
                                                    return;
                                                }
                                                void handleSelectCamera(camera);
                                            }}
                                        >
                                            <ListItemText primary={camera.name} secondary={camera.type} />
                                        </ListItemButton>
                                    )}
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CameraDialog;
